import React, { useState, useEffect } from 'react';
import optionApi from '../../../services/optionApi';
import productApi from '../../../services/productApi';
import OptionGroupCard from './option-group-card';
import OptionGroupUpsertModal from './option-group-upsert-modal';
import OptionGroupExistModal from './option-group-exist-modal';
import { useSelector } from 'react-redux';
import Notification from '../../notification/notification';
import { TWButton } from '../../ui';
import { OptionGroupListSkeleton } from '../../skeleton/skeleton';
import { capitalizeWords, capitalizeSentence } from '../../../utils/baseUtils';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';

export default function OptionGroupList({
  selected,
  setSelected,
  onModifierCountChange,
}) {
  const globalCompany = useSelector((state) => state.companyReducer.selected);
  const [notf, setNotf] = useState({
    display: false,
    type: 'error',
    message: '',
  });
  const [errors, setErrors] = useState({
    name: false,
    min: false,
    max: false,
  });
  const [loading, setLoading] = useState({
    optionGroupLoading: false,
    optionsLoading: false,
    upsertLoading: false,
    deleteLoading: false,
    visibleLoading: false,
    storeLoading: false,
  });

  const [dialogs, setDialogs] = useState({
    optionGroupUpsert: false,
    optionGroupDelete: false,
    optionGroupExist: false,
    optionVisible: false,
    hidedateDelete: false,
  });

  const [optionGroupForm, setOptionGroupForm] = useState({
    name: '',
    displayorder: '',
    description: '',
    min: '',
    max: '',
    allowquantities: false,
    companyid: globalCompany.id,
    options: [],
    productoptiongroups: [],
    storeids: [],
  });

  const getOptionGroupList = async () => {
    if (!selected.product?.id) return;
    try {
      setLoading((prev) => ({ ...prev, optionGroupLoading: true }));
      const list = await productApi.getById(selected.product.id);
      setOptionGroupForm((prev) => ({
        ...prev,
        productoptiongroups: list.data.data.productoptiongroups,
      }));
    } catch (error) {
      setNotf((prev) => ({
        ...prev,
        message: error,
        display: true,
      }));
    } finally {
      setLoading((prev) => ({ ...prev, optionGroupLoading: false }));
    }
  };

  const onClickDuplicateGroup = async (group) => {
    try {
      const res = await optionApi.duplicateGroup(group.id);
      const newName = res?.data?.data?.name || res?.data?.name || 'group';
      setNotf({
        display: true,
        type: 'success',
        message: `Duplicated as "${newName}"`,
      });
      await getOptionGroupList();
    } catch (e) {
      setNotf({
        display: true,
        type: 'error',
        message:
          e?.response?.data?.Message ||
          e?.response?.data ||
          'Duplicate failed',
      });
    }
  };

  const onClickOpenOptionGroupUpsertModal = (optionGroup) => {
    setOptionGroupForm({
      ...optionGroup,
      options: [],
      storeids:
        optionGroup?.optionGroupStores?.map((store) => ({
          value: store.storeid,
          label: store.store?.name,
        })) || [],
      productoptiongroups: optionGroupForm.productoptiongroups,
    });
    setDialogs((prev) => ({ ...prev, optionGroupUpsert: true }));
  };

  const onClickOptionGroupFormSubmit = async () => {
    if (
      !optionGroupForm.name ||
      optionGroupForm.min === '' ||
      optionGroupForm.max === ''
    ) {
      setErrors((prev) => ({
        ...prev,
        name: !optionGroupForm.name ? true : false,
        min: optionGroupForm.min === '' ? true : false,
        max: optionGroupForm.max === '' ? true : false,
      }));
      return;
    } else {
      setErrors((prev) => ({
        ...prev,
        name: false,
        min: false,
        max: false,
      }));
    }
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const body = {
        ...optionGroupForm,
        name: optionGroupForm.name
          ? capitalizeWords(optionGroupForm.name.trim())
          : optionGroupForm.name.trim(),
        description: optionGroupForm.description
          ? capitalizeSentence(optionGroupForm.description.trim())
          : optionGroupForm.description || '',
        storeids: Array.isArray(optionGroupForm.storeids)
          ? optionGroupForm.storeids.map((store) => store.value ?? store)
          : [],
      };
      const optionGroup = await optionApi.upsertGroup(body);

      optionGroupForm.options.forEach(async (item) => {
        const body = {
          ...item,
          optiongroupid: optionGroup.data.data.id,
          name: item.name ? capitalizeWords(item.name.trim()) : item.name,
        };
        await optionApi.upsert(body);
      });

      if (!optionGroupForm.id) {
        await linkProductToOptionGroup(optionGroup.data.data);
      }
    } catch (error) {
    } finally {
      setOptionGroupForm({
        options: [],
        productoptiongroups: optionGroupForm.productoptiongroups,
      });
      getOptionGroupList();
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      setDialogs((prev) => ({ ...prev, optionGroupUpsert: false }));
    }
  };

  const linkProductToOptionGroup = async (optionGroup) => {
    let optiongroupids = [];

    optionGroupForm.productoptiongroups.map((optionGroup) =>
      optiongroupids.push(optionGroup.optiongroupid),
    );
    optiongroupids.push(optionGroup.id);

    const formData = new FormData();

    for (const key of Object.keys(selected.product)) {
      if (
        (key === 'description' || key === 'hidedate') &&
        (selected.product[key] === '' || selected.product[key] === null)
      ) {
        formData.append(key, '');
      } else if (key === 'libraryimageid') {
        // Nullable FK — never send the literal "null" (violates
        // fk_products_libraryimage). Empty string = no library link.
        const v = selected.product[key];
        formData.append('libraryimageid', v && v !== 'null' ? v : '');
      } else {
        // Coerce null/undefined to '' so they aren't sent as the string "null".
        const v = selected.product[key];
        formData.append(key, v === null || v === undefined ? '' : v);
      }
    }

    optiongroupids.forEach((id) => {
      formData.append('optiongroupids', id);
    });

    const config = {
      headers: { 'Content-Type': 'multipart/form-data' },
    };

    await productApi.upsert(formData, config);
  };

  const onClickOpenOptionGroupExistModal = () => {
    setDialogs((prev) => ({ ...prev, optionGroupExist: true }));
  };

  const onClickOptionGroupExistSubmit = async () => {
    try {
      setLoading((prev) => ({ ...prev, upsertLoading: true }));
      const optiongroupids = optionGroupForm.optiongroupids;

      const formData = new FormData();

      for (const key of Object.keys(selected.product)) {
        if (
          (key === 'description' || key === 'hidedate') &&
          (selected.product[key] === '' || selected.product[key] === null)
        ) {
          formData.append(key, '');
        } else if (key === 'libraryimageid') {
          // Nullable FK — never send the literal "null" (violates
          // fk_products_libraryimage). Empty string = no library link.
          const v = selected.product[key];
          formData.append('libraryimageid', v && v !== 'null' ? v : '');
        } else {
          // Coerce null/undefined to '' so they aren't sent as the string "null".
          const v = selected.product[key];
          formData.append(key, v === null || v === undefined ? '' : v);
        }
      }

      if (optiongroupids.length > 0) {
        optiongroupids.forEach((id) => {
          formData.append('optiongroupids', id);
        });
      } else {
        formData.append('optiongroupids', '');
      }

      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
      };

      const optionGroup = await productApi.upsert(formData, config);

      if (optionGroup.data.data) {
        setDialogs((prev) => ({
          ...prev,
          optionGroupExist: false,
        }));
      }
    } catch (error) {
    } finally {
      setLoading((prev) => ({ ...prev, upsertLoading: false }));
      await getOptionGroupList();
    }
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 5 },
    }),
  );

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const groups = optionGroupForm.productoptiongroups.filter(
      (x) => x.optiongroup.isdeleted === false,
    );
    const oldIndex = groups.findIndex((g) => g.optiongroup.id === active.id);
    const newIndex = groups.findIndex((g) => g.optiongroup.id === over.id);
    const reordered = arrayMove(groups, oldIndex, newIndex);

    setOptionGroupForm((prev) => ({
      ...prev,
      productoptiongroups: reordered,
    }));

    try {
      await optionApi.reorderGroup({
        items: reordered.map((g, index) => ({
          id: g.optiongroup.id,
          displayorder: index,
        })),
      });
    } catch (error) {
      console.error('Error reordering option groups:', error);
      await getOptionGroupList();
    }
  };

  useEffect(() => {
    getOptionGroupList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected.product.id]);

  const visibleOptionGroups =
    optionGroupForm?.productoptiongroups?.filter(
      (x) => x.optiongroup.isdeleted === false,
    ) || [];

  useEffect(() => {
    if (typeof onModifierCountChange === 'function') {
      onModifierCountChange(visibleOptionGroups.length);
    }
  }, [visibleOptionGroups.length, onModifierCountChange]);

  return (
    <>
      <div className="pb-3 pt-2">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-slate-200 bg-gradient-to-r from-white to-slate-50 px-4 py-3 dark:border-slate-700 dark:from-slate-800 dark:to-slate-800/80">
          <div>
            <p className="text-[0.65rem] font-semibold uppercase tracking-[0.14em] text-slate-500 dark:text-slate-400">
              Item Modifiers
            </p>
            <h1 className="mt-0.5 text-lg font-semibold text-slate-900 dark:text-slate-100">
              Modifier Groups
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Attach existing groups or create a new modifier flow.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center rounded-md border border-slate-300 bg-white px-2.5 py-1 text-xs font-semibold text-slate-600 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200">
              {visibleOptionGroups.length} Group
              {visibleOptionGroups.length > 1 ? 's' : ''}
            </span>
            <TWButton
              variant="default"
              size="sm"
              label="+ Modifiers"
              onClick={onClickOpenOptionGroupExistModal}
            />
            <TWButton
              variant="primary"
              size="sm"
              label="+ Add New"
              onClick={() =>
                onClickOpenOptionGroupUpsertModal({
                  name: '',
                  displayorder:
                    optionGroupForm.productoptiongroups?.length > 0
                      ? optionGroupForm.productoptiongroups[
                          optionGroupForm.productoptiongroups.length - 1
                        ]?.optiongroup?.displayorder + 1
                      : 1,
                  min: '',
                  max: '',
                  allowquantities: false,
                  companyid: globalCompany.id,
                })
              }
            />
          </div>
        </div>

        {loading.optionGroupLoading ? (
          <OptionGroupListSkeleton />
        ) : visibleOptionGroups.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToVerticalAxis]}
          >
            <SortableContext
              items={visibleOptionGroups.map((g) => g.optiongroup.id)}
              strategy={verticalListSortingStrategy}
            >
              <div>
                {visibleOptionGroups.map((optionGroup, i) => (
                  <OptionGroupCard
                    key={optionGroup.id}
                    index={i}
                    id={optionGroup.id}
                    sortableId={optionGroup.optiongroup.id}
                    optionGroup={optionGroup.optiongroup}
                    onClickOpenOptionGroupUpsertModal={
                      onClickOpenOptionGroupUpsertModal
                    }
                    onClickDuplicateGroup={onClickDuplicateGroup}
                    selected={selected}
                    setSelected={setSelected}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <div className="rounded-md border border-dashed border-slate-300 bg-white/90 px-6 py-10 text-center dark:border-slate-600 dark:bg-slate-800">
            <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
              No modifier group yet
            </p>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Add an existing group or create a new one for this product.
            </p>
          </div>
        )}
      </div>

      {dialogs.optionGroupUpsert && (
        <OptionGroupUpsertModal
          show={dialogs.optionGroupUpsert}
          optionGroupForm={optionGroupForm}
          setOptionGroupForm={setOptionGroupForm}
          getOptionGroupList={getOptionGroupList}
          dialogs={dialogs}
          setDialogs={setDialogs}
          loading={loading}
          errors={errors}
          setErrors={setErrors}
          setLoading={setLoading}
          onClickOptionGroupFormSubmit={onClickOptionGroupFormSubmit}
        />
      )}
      {dialogs.optionGroupExist && (
        <OptionGroupExistModal
          show={dialogs.optionGroupExist}
          optionGroupForm={optionGroupForm}
          setOptionGroupForm={setOptionGroupForm}
          onClickOptionGroupExistSubmit={onClickOptionGroupExistSubmit}
          notf={notf}
          setNotf={setNotf}
          setDialogs={setDialogs}
          storeIds={selected.menuStoreIds}
        />
      )}
      <Notification
        display={notf.display}
        type={notf.type}
        message={notf.message}
        onClose={() => setNotf((prev) => ({ ...prev, display: false }))}
      />
    </>
  );
}
